import React from "react";
import "./i18n";
import {
  BrowserRouter,
  Routes,
  Route,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import Layout from "./components/Layout/index.tsx";
import "./leaflet-config";
import "leaflet/dist/leaflet.css";
import RegionSelector from "./pages/RegionSelector/index.tsx";
import RegionMap from "./pages/RegionMap/index.tsx";
import RegionRedirect from "./components/RegionRedirect.tsx";
import { AuthProvider } from "./contexts/AuthContext.tsx";
import { GroupProvider, useGroup } from "./contexts/GroupContext.tsx";
import CommunityEntryPoint from "./components/CommunityEntryPoint.tsx";
import TrailPreferencesPage from "./pages/TrailPreferencesPage/index.tsx";
import ContactReport from "./components/ContactReport";
import MontiAlburniPage from "./pages/MontiAlburniPage";
// dentro <Routes>
const RegionMapWrapper: React.FC = () => {
  const {
    name,
    groupId: groupIdFromPath,
    id: poiIdFromPath,
  } = useParams<{
    name: string;
    groupId?: string;
    id?: string;
  }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { groupId: contextGroupId } = useGroup(); // ← prende groupId dal context

  const match = location.pathname.match(/\/(trekkings|experiences)/);
  const contentType = match
    ? (match[1] as "trekkings" | "experiences")
    : "trekkings";

  // Priorità: path > query param (?community, ?g) > context
  const groupId =
    groupIdFromPath ||
    searchParams.get("community") ||
    searchParams.get("g") ||
    contextGroupId ||
    undefined;

  const focusTrailId = searchParams.get("trailId") || undefined;

  // Estrai focusPoiId da hash (#poi-node/ID, #poi-way/ID, #poi-relation/ID, #poi-node:ID, ecc)
  function extractPoiIdFromHash(hash) {
    if (!hash) return undefined;
    // Esempi: #poi-node/123, #poi-way:456, #poi-relation/789, #poi-123
    const m = hash.match(/^#?poi-((node|way|relation)[/:]\d+|\d+)$/i);
    return m ? m[1] : undefined;
  }
  const focusPoiIdFromHash = extractPoiIdFromHash(window.location.hash);
  // Se presente nella path, priorità a id, poi hash, poi query param
  const focusPoiId =
    poiIdFromPath ||
    focusPoiIdFromHash ||
    searchParams.get("poiId") ||
    undefined;

  // Aggiorna focusPoiId su hashchange e chiama direttamente window.focusPoi se presente
  const [, setHash] = React.useState(window.location.hash);
  React.useEffect(() => {
    const handler = () => {
      setHash(window.location.hash);
      const poiId = extractPoiIdFromHash(window.location.hash);
      if (poiId && window.focusPoi) {
        // Delay per assicurarsi che i marker siano pronti
        setTimeout(() => {
          window.focusPoi(poiId);
        }, 200);
      }
    };
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  return (
    <RegionMap
      key={`${name}-${contentType}`}
      contentType={contentType}
      groupId={groupId}
      focusTrailId={focusTrailId}
      focusPoiId={focusPoiId}
    />
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <GroupProvider>
        <BrowserRouter>
          <Layout>
            <Routes>
              <Route path="/" element={<RegionSelector />} />
              <Route
                path="/community/:groupId"
                element={<CommunityEntryPoint />}
              />
              <Route path="/region/:name" element={<RegionRedirect />} />
              <Route
                path="/region/:name/trekkings"
                element={<RegionMapWrapper />}
              />
              <Route
                path="/region/:name/experiences"
                element={<RegionMapWrapper />}
              />
              <Route
                path="/region/:name/trekkings/:groupId"
                element={<RegionMapWrapper />}
              />
              <Route
                path="/region/:name/poi/:id"
                element={<RegionMapWrapper />}
              />
              <Route
                path="/trekkings/:groupId/preferences"
                element={<TrailPreferencesPage />}
              />
              <Route path="/preferences" element={<TrailPreferencesPage />} />
              <Route path="/admin/contact-report" element={<ContactReport />} />
              <Route
                path="/region/campania/monti-alburni"
                element={<MontiAlburniPage />}
              />
            </Routes>
          </Layout>
        </BrowserRouter>
      </GroupProvider>
    </AuthProvider>
  );
};

export default App;
